import { Component, Input, EventEmitter, Output, OnDestroy, OnChanges } from '@angular/core';
import { Subscription, throwError } from 'rxjs';
import { ERRORS } from '../../models/constants';
import { FormGroup } from '@angular/forms';
import { distinctUntilChanged } from 'rxjs/operators';
import { CoreCommonService } from '../../providers/services/common.service';
import { CoreHttpService } from '../../utilities/services/http.service';

@Component({
  selector: 'app-checkbox',
  templateUrl: './checkbox.component.html'
})
export class CheckboxComponent implements OnDestroy, OnChanges {
  @Input() field;
  @Input() form;
  @Input() type;
  @Input() dynamicControlScope: any;
  @Input() parentScope: any;
  @Output() controlReady = new EventEmitter<boolean>();
  subscription: Subscription;

  constructor(private coreCommonService: CoreCommonService, private coreHttpService: CoreHttpService) {}

  /**
   * A callback method that is invoked if any data-bound properties change
   */
  ngOnChanges() {
    this.handleControlLoad();
  }

  /**
   * This method will handle control load
   * NOTE: Sequence of function calls need to be exactly same as below
   */
  private async handleControlLoad() {
    await this.registerEvents();
    this.notifyControlLoadComplete();
  }

  /**
   * This method will register the events
   */
  private async registerEvents() {
    try {
      const valueChange = this.registerValueChange(this.field, this.form);
      await Promise.all([valueChange]);
      return true;
    } catch (error) {
      console.log(error);
    }
  }

  /**
   * This method will notify when control is ready
   */
  private notifyControlLoadComplete() {
    this.controlReady.emit(true);
  }

  /**
   * getter function to check mandatory fields
   */
  get isMandatory(): boolean {
    try {
      return this.field.validations.mandatory.value;
    } catch (error) {
      return false;
    }
  }

  /**
   * Dynamic function to call the function by name
   */
  async callFunctionByName(fnName: string, ...params: any) {
    try {
      let scope;
      if (this[fnName]) {
        scope = this;
      } else if (this.dynamicControlScope[fnName]) {
        scope = this.dynamicControlScope;
      } else if (this.parentScope) {
        scope = this.parentScope;
      }
      params ? scope[fnName](...params) : scope[fnName]();
    } catch (error) {
      console.log(`${ERRORS.FUNCTION_CALL_FAILED}: ${error}`);
    }
    return true;
  }
  /**
   * This method will register checkbox value change
   */
  private registerValueChange(field: object, form: FormGroup) {
    try {
      const control = form.get(field['logicalName']);
      const onChangeEventSchema = field['onChangeEvent'] || [];
      if (control && field.hasOwnProperty('onChangeEvent') && field['onChangeEvent']?.length > 0) {
        this.subscription = control.valueChanges.pipe(distinctUntilChanged()).subscribe((value) => {
          for (const eventSchema of onChangeEventSchema) {
            this.callFunctionByName(eventSchema.functionName, field, value, form, eventSchema.params);
          }
        });
      }
      return true;
    } catch (error) {
      throwError(error);
    }
  }

  /**
   * Unsubscribe Observables and detach event handlers to avoid memory leaks
   */
  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }
}
